import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";

import {
	TextField,
	InputAdornment,
	Select,
	MenuItem,
	Button,
	Switch,
	FormControlLabel,
	IconButton as MuiIconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions
} from "@mui/material";
import { Pagination } from "@mui/material";
import { Search, Add, DeleteOutline, KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { THEME_MAP } from "../theme";

import { useUsers, useUserUtils } from "../actions/users";
import { useIsMobile } from "../hooks/useIsMobile";
import { captureError } from "../lib/errorReporting";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

export function Users() {
	const history = useHistory();
	const { search } = useLocation();
	const { users, endOfResults, loading, totalCount } = useUsers();
	const { getNext, setUsersQuery, deleteUser, addUser, refreshSearch } = useUserUtils();
	const isMobile = useIsMobile();

	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [currentPage, setCurrentPage] = useState(1);
	const [pendingPage, setPendingPage] = useState(null);
	const [pendingLast, setPendingLast] = useState(false);
	const [lastQuery, setLastQuery] = useState("");
	const [expandedUserId, setExpandedUserId] = useState(null);
	const [sortColumn, setSortColumn] = useState("email");
	const [sortDirection, setSortDirection] = useState("asc");
	const [showAddDialog, setShowAddDialog] = useState(false);
	const [addEmail, setAddEmail] = useState("");
	const [addLoading, setAddLoading] = useState(false);
	const [addError, setAddError] = useState("");
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [deleteError, setDeleteError] = useState("");

	const canPaginate = !search;
	const totalPages = Math.max(
		1,
		Math.ceil(((totalCount ?? users?.length ?? 0) || 0) / pageSize)
	);
	const paginationCount = totalPages;
	const paginationPage = Math.min(Math.max(1, currentPage), paginationCount);

	const handlePageChange = useCallback((_, value) => {
		setPendingLast(false);
		setPendingPage(null);

		const requiredCount = (value - 1) * pageSize + 1;
		const beyondLoaded = requiredCount > (users?.length || 0);
		const aimingForEnd = value >= paginationCount;

		if (aimingForEnd && !endOfResults) {
			setPendingLast(true);
			if (!loading) {
				getNext();
			}
			return;
		}

		if (beyondLoaded && !endOfResults) {
			setPendingPage(value);
			if (!loading) {
				getNext();
			}
			return;
		}

		setCurrentPage(Math.min(value, paginationCount));
	}, [endOfResults, getNext, loading, pageSize, paginationCount, users?.length]);

	const handlePageSizeChange = useCallback((event) => {
		const nextSize = Number(event.target.value) || DEFAULT_PAGE_SIZE;
		setPageSize(nextSize);
	}, []);

	const handleSearch = useCallback(
		(e) => {
			const q = e.target.value || "";
			setUsersQuery(q);
			history.replace({ search: q ? `?q=${encodeURIComponent(q)}` : "" });
			setCurrentPage(1);
			setPendingPage(null);
			setPendingLast(false);
		},
		[history, setUsersQuery]
	);

	useEffect(() => {
		const hasQuery = search.match(/q=([\w\W]+?)(&|$)/);
		if (hasQuery) {
			const q = decodeURIComponent(hasQuery[1]);
			if (q === lastQuery) return;
			setLastQuery(q);
			setUsersQuery(q);
			history.replace({ search: q ? `?q=${encodeURIComponent(q)}` : "" });
			setCurrentPage(1);
			setPendingPage(null);
			setPendingLast(false);
			return;
		}

		// No query; just ensure initial load once
		if (!users?.length && !loading) getNext();
	}, [getNext, history, lastQuery, loading, search, setUsersQuery, users?.length]);

	useEffect(() => {
		if (currentPage > paginationCount) {
			setCurrentPage(paginationCount);
		}
	}, [currentPage, paginationCount]);

	// For email asc, the server already returns sorted data — no client sort needed.
	// For email desc or admin sort, we need ALL data loaded first.
	const isServerSorted = sortColumn === "email" && sortDirection === "asc";

	useEffect(() => {
		if (isServerSorted || endOfResults || loading) return;
		// Auto-load all remaining users so client-side sort works on the full dataset
		getNext();
	}, [isServerSorted, endOfResults, loading, getNext]);

	const sortedUsers = useMemo(() => {
		const all = users || [];
		if (isServerSorted || !sortColumn) return all;

		return [...all].sort((a, b) => {
			const dataA = a.data() || {};
			const dataB = b.data() || {};
			let valA, valB;

			switch (sortColumn) {
				case "email":
					valA = (dataA.email || "").toLowerCase();
					valB = (dataB.email || "").toLowerCase();
					break;
				case "admin":
					valA = dataA.isAdmin ? 1 : 0;
					valB = dataB.isAdmin ? 1 : 0;
					break;
				default:
					return 0;
			}

			if (valA < valB) return sortDirection === "asc" ? -1 : 1;
			if (valA > valB) return sortDirection === "asc" ? 1 : -1;
			return 0;
		});
	}, [users, sortColumn, sortDirection, isServerSorted]);

	const paginatedUsers = useMemo(() => {
		const start = (paginationPage - 1) * pageSize;
		return sortedUsers.slice(start, start + pageSize);
	}, [paginationPage, pageSize, sortedUsers]);

	const pageStartIndex = paginatedUsers.length
		? (paginationPage - 1) * pageSize + 1
		: 0;
	const pageEndIndex = paginatedUsers.length
		? Math.min(
			pageStartIndex + pageSize - 1,
			totalCount ?? (paginationPage - 1) * pageSize + paginatedUsers.length
		)
		: 0;

	const pageSummaryText = useMemo(() => {
		if (loading && (users?.length || 0) === 0) {
			return "Loading users...";
		}
		if ((users?.length || 0) === 0) {
			return "No users to display";
		}
		if ((pendingLast || pendingPage !== null) && !endOfResults) {
			return "Fetching more users...";
		}
		const knownTotal = totalCount ?? users.length;
		const suffix = totalCount === null && !endOfResults ? `${users.length}+` : knownTotal;
		return `${pageStartIndex}-${pageEndIndex} of ${suffix} users`;
	}, [endOfResults, loading, pageEndIndex, pageStartIndex, pendingLast, pendingPage, totalCount, users?.length]);

	const userStats = useMemo(() => {
		const all = users || [];
		if (!all.length) return null;
		let male = 0, female = 0, otherGender = 0;
		let single = 0, taken = 0, otherStatus = 0;
		let ageSum = 0, ageCount = 0;

		all.forEach((snap) => {
			const d = snap.data() || {};
			const g = (d.gender || "").toLowerCase();
			if (g === "male") male++;
			else if (g === "female") female++;
			else if (g) otherGender++;

			const r = (d.relationshipStatus || "").toLowerCase();
			if (r === "single") single++;
			else if (r === "taken" || r === "in a relationship" || r === "married") taken++;
			else if (r) otherStatus++;

			const raw = d.birthday || d.birthdate || d.birthday_at;
			if (raw) {
				try {
					const bd = raw?.toDate ? raw.toDate() : new Date(raw);
					if (!isNaN(bd)) {
						const age = Math.floor((Date.now() - bd.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
						if (age > 0 && age < 120) { ageSum += age; ageCount++; }
					}
				} catch { /* skip */ }
			}
		});

		const avgAge = ageCount > 0 ? Math.round(ageSum / ageCount) : null;
		return { male, female, otherGender, single, taken, otherStatus, avgAge };
	}, [users]);

	useEffect(() => {
		if (!canPaginate || loading) {
			return;
		}

		if (pendingLast) {
			if (endOfResults) {
				setCurrentPage(Math.max(1, totalPages));
				setPendingLast(false);
			} else {
				getNext();
			}
		}
	}, [canPaginate, endOfResults, getNext, loading, pendingLast, totalPages]);

	useEffect(() => {
		if (!canPaginate || loading || pendingLast || pendingPage === null) {
			return;
		}

		const requiredCount = (pendingPage - 1) * pageSize + 1;
		if ((users?.length || 0) >= requiredCount || endOfResults) {
			setCurrentPage(Math.min(pendingPage, totalPages));
			setPendingPage(null);
			return;
		}

		getNext();
	}, [canPaginate, endOfResults, getNext, loading, pageSize, pendingLast, pendingPage, totalPages, users?.length]);

	useEffect(() => {
		setCurrentPage(1);
		setPendingPage(null);
		setPendingLast(false);
	}, [pageSize]);

	useEffect(() => {
		if (!canPaginate || loading || endOfResults) {
			return;
		}

		const requiredCount = paginationPage * pageSize;
		if ((users?.length || 0) < requiredCount) {
			getNext();
		}
	}, [canPaginate, endOfResults, getNext, loading, pageSize, paginationPage, users?.length]);

	const handleSort = useCallback((column) => {
		setSortColumn((prev) => {
			if (prev === column) {
				setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
				return prev;
			}
			setSortDirection("asc");
			return column;
		});
	}, []);

	const handleAddUser = useCallback(async () => {
		const email = (addEmail || "").trim().toLowerCase();
		if (!email) {
			setAddError("Email is required");
			return;
		}
		setAddLoading(true);
		setAddError("");
		try {
			await addUser(email);
			setShowAddDialog(false);
			setAddEmail("");
			refreshSearch();
		} catch (err) {
			captureError(err, { view: "Users", action: "addUser" });
			setAddError(err?.message || "Failed to add user");
		} finally {
			setAddLoading(false);
		}
	}, [addEmail, addUser, refreshSearch]);

	const handleDeleteUser = useCallback(async () => {
		if (!deleteTarget) return;
		setDeleteLoading(true);
		setDeleteError("");
		try {
			await deleteUser(deleteTarget.id);
			setDeleteTarget(null);
		} catch (err) {
			captureError(err, { view: "Users", action: "deleteUser" });
			setDeleteError(err?.message || "Failed to delete user");
		} finally {
			setDeleteLoading(false);
		}
	}, [deleteTarget, deleteUser]);

	const COLUMNS = [
		{ key: "email", label: "Email / Name", flex: "1 1 auto", sortable: true },
		{ key: "pills", label: "", flex: "0 0 auto", sortable: false },
		{ key: "admin", label: "Admin", flex: "0 0 80px", sortable: true },
	];

	return (
		<Container $isMobile={isMobile}>
			<div className="filters">
				<TextField
					className="input"
					placeholder="Filter by email"
					variant="outlined"
					size="small"
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search>Filled</Search>
							</InputAdornment>
						)
					}}
					onChange={handleSearch}
				/>
			</div>
			{userStats ? (
				<div className="stats-bar">
					<span className="stat-group">
						<span className="stat-pill male">{userStats.male} Male</span>
						<span className="stat-pill female">{userStats.female} Female</span>
						{userStats.otherGender > 0 ? <span className="stat-pill other">{userStats.otherGender} Other</span> : null}
					</span>
					<span className="stat-group">
						<span className="stat-pill single">{userStats.single} Single</span>
						<span className="stat-pill taken">{userStats.taken} Taken</span>
						{userStats.otherStatus > 0 ? <span className="stat-pill relationship-other">{userStats.otherStatus} Other</span> : null}
					</span>
					{userStats.avgAge !== null ? (
						<span className="stat-group">
							<span className="stat-pill muted">Avg Age: {userStats.avgAge}</span>
						</span>
					) : null}
				</div>
			) : null}
			<div className="results-meta">
				<span>{pageSummaryText}</span>
				{loading ? <span className="loading-indicator">Loading...</span> : null}
				<MuiIconButton
					size="small"
					onClick={() => { setShowAddDialog(true); setAddEmail(""); setAddError(""); }}
					sx={{ color: "#1976d2", marginLeft: "auto" }}
					title="Add user"
				>
					<Add />
				</MuiIconButton>
			</div>

			<div className="users-container">
				<div className="table-header">
					{COLUMNS.map((col) => (
						<div
							key={col.key}
							className={`header-cell ${sortColumn === col.key ? "active" : ""} ${!col.sortable ? "no-sort" : ""}`}
							style={{ flex: col.flex }}
							onClick={() => col.sortable && handleSort(col.key)}
						>
							{col.label ? <span>{col.label}</span> : null}
							{col.sortable && sortColumn === col.key ? (
								sortDirection === "asc" ? <KeyboardArrowUp className="sort-icon" /> : <KeyboardArrowDown className="sort-icon" />
							) : null}
						</div>
					))}
					<div className="header-cell actions-header no-sort" style={{ flex: "0 0 44px" }} />
				</div>
				{paginatedUsers.map((snapshot) => (
					<UserListItem
						key={snapshot.id}
						userSnapshot={snapshot}
						expanded={expandedUserId === snapshot.id}
						onToggle={() => setExpandedUserId(expandedUserId === snapshot.id ? null : snapshot.id)}
						columns={COLUMNS}
						onDelete={(user) => { setDeleteTarget(user); setDeleteError(""); }}
					/>
				))}
				{loading && paginatedUsers.length === 0 && (users?.length || 0) > 0 && (
					<div className="no-results">Loading page...</div>
				)}
				{(users?.length || 0) === 0 && !loading && (
					<div className="no-results">No users found</div>
				)}
			</div>

			<div className="pagination-footer">
				<div className="rows-per-page">
					<span className="label">Rows per page</span>
						<Select size="small" value={pageSize} onChange={handlePageSizeChange} disabled={!canPaginate}>
						{PAGE_SIZE_OPTIONS.map((option) => (
							<MenuItem key={option} value={option}>
								{option}
							</MenuItem>
						))}
					</Select>
				</div>
				<div className="page-controls">
					<span className="page-summary">{pageSummaryText}</span>
					<Pagination
						count={paginationCount}
						page={paginationPage}
						onChange={handlePageChange}
						color="primary"
						shape="rounded"
						showFirstButton
						showLastButton
						siblingCount={1}
						boundaryCount={1}
							disabled={!canPaginate || (loading && (users?.length || 0) === 0)}
					/>
				</div>
			</div>

			{/* Add User Dialog */}
			<Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Add User</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ mb: 2 }}>
						Enter the email address for the new user. If they don&apos;t have an account, one will be created. A password reset email will be sent so they can set their own password.
					</DialogContentText>
					<TextField
						autoFocus
						label="Email"
						type="email"
						size="small"
						fullWidth
						value={addEmail}
						onChange={(e) => setAddEmail(e.target.value)}
						error={!!addError}
						helperText={addError}
						onKeyDown={(e) => { if (e.key === "Enter") handleAddUser(); }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setShowAddDialog(false)} disabled={addLoading}>Cancel</Button>
					<Button variant="contained" onClick={handleAddUser} disabled={addLoading}>
						{addLoading ? "Adding..." : "Add"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete User Confirmation Dialog */}
			<Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
				<DialogTitle>Delete User</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete <strong>{deleteTarget?.email || "this user"}</strong>? This will remove their profile, profile image, and authentication. This action cannot be undone.
					</DialogContentText>
					{deleteError ? (
						<DialogContentText sx={{ color: "error.main", mt: 1, fontWeight: 600 }}>
							{deleteError}
						</DialogContentText>
					) : null}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>Cancel</Button>
					<Button variant="contained" color="error" onClick={handleDeleteUser} disabled={deleteLoading}>
						{deleteLoading ? "Deleting..." : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

const Container = styled.div`
	height: 100%;
	width: 100%;

	overflow: auto;

	padding: 24px;

	.input {
		background: #fff;
	}

	.filters {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 16px;
	}

	.users-container {
		width: 100%;
		min-height: 100px;
		border-radius: 4px;
		border: 1px solid ${THEME_MAP["color-basic-400"]};
		background: ${THEME_MAP["color-basic-200"]};

		margin-bottom: 16px;

		max-height: calc(100% - 125px);
		overflow-y: scroll;
	}

	.table-header {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 8px 16px;
		background: ${THEME_MAP["color-basic-200"]};
		border-bottom: 2px solid ${THEME_MAP["color-basic-400"]};
		position: sticky;
		top: 0;
		z-index: 1;

		.header-cell {
			display: flex;
			align-items: center;
			gap: 4px;
			font-weight: 700;
			font-size: 0.85rem;
			color: ${THEME_MAP["color-basic-700"]};
			cursor: pointer;
			user-select: none;
			white-space: nowrap;

			&:hover {
				color: ${THEME_MAP["color-basic-900"]};
			}

			&.active {
				color: ${THEME_MAP["color-primary-700"]};
			}

			.sort-icon {
				font-size: 0.95rem;
			}
		}

		.actions-header,
		.no-sort {
			cursor: default;
			&:hover {
				color: ${THEME_MAP["color-basic-700"]};
			}
		}
	}

	.stats-bar {
		display: flex;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
		margin-bottom: 8px;
	}

	.stat-group {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.stat-pill {
		display: inline-flex;
		align-items: center;
		padding: 2px 8px;
		border-radius: 999px;
		font-weight: 600;
		font-size: 0.8rem;
	}

	.stat-pill.male {
		color: ${THEME_MAP["color-info-700"]};
		background: ${THEME_MAP["color-info-transparent-200"]};
	}

	.stat-pill.female {
		color: ${THEME_MAP["color-danger-700"]};
		background: ${THEME_MAP["color-danger-transparent-200"]};
	}

	.stat-pill.other {
		color: ${THEME_MAP["color-info-700"]};
		background: ${THEME_MAP["color-info-transparent-200"]};
	}

	.stat-pill.single {
		color: ${THEME_MAP["color-warning-800"]};
		background: ${THEME_MAP["color-warning-transparent-200"]};
	}

	.stat-pill.taken {
		color: ${THEME_MAP["color-success-800"]};
		background: ${THEME_MAP["color-success-transparent-200"]};
	}

	.stat-pill.relationship-other {
		color: ${THEME_MAP["color-basic-800"]};
		background: ${THEME_MAP["color-basic-300"]};
	}

	.stat-pill.muted {
		color: ${THEME_MAP["color-basic-700"]};
		background: ${THEME_MAP["color-basic-300"]};
	}

	.results-meta {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
		color: ${THEME_MAP["color-basic-600"]};
	}

	.pagination-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		flex-wrap: wrap;
		padding: 12px 0;
		border-top: 1px solid ${THEME_MAP["color-basic-300"]};
	}

	.rows-per-page {
		display: flex;
		align-items: center;
		gap: 8px;
		color: ${THEME_MAP["color-basic-700"]};
		font-weight: 600;
	}

	.page-controls {
		display: flex;
		align-items: center;
		gap: 12px;
		flex: 1;
		justify-content: flex-end;
		min-width: 260px;
		flex-wrap: wrap;
	}

	.page-summary {
		color: ${THEME_MAP["color-basic-700"]};
		white-space: nowrap;
	}

	@media (max-width: 767px) {
		padding: 12px;

		.users-container {
			overflow-x: auto;
		}

		.table-header,
		.row-main {
			min-width: 520px;
		}

		.pagination-footer {
			flex-direction: column;
			align-items: stretch;
			gap: 8px;
			padding: 10px 0;
		}

		.rows-per-page {
			justify-content: center;
		}

		.page-controls {
			flex-direction: column;
			align-items: center;
			min-width: 0;
			gap: 6px;
		}

		.page-controls .MuiPagination-root {
			.MuiPaginationItem-root {
				min-width: 28px;
				height: 28px;
				font-size: 0.8rem;
			}
		}
	}

	.loading-indicator {
		color: ${THEME_MAP["color-primary-600"]};
		font-weight: 600;
		margin-left: 8px;
	}

	.no-results {
		padding: 12px 16px;
		color: ${THEME_MAP["color-basic-700"]};
	}

	.user-row {
		display: flex;
		flex-direction: column;
		background: transparent;
		position: relative;
		cursor: pointer;
		border-radius: 2px;
		margin: 4px 0;

		.name-email {
			display: flex;
			flex-direction: column;
			gap: 4px;
			word-break: break-word;
			color: inherit;

			.email {
				font-weight: 600;
			}
			.name {
				color: ${THEME_MAP["color-basic-600"]};
				font-size: 0.95rem;
			}
		}

		.cell {
			display: flex;
			align-items: center;
			font-size: 0.95rem;
			color: inherit;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.pills-cell {
			flex: 0 0 auto;
			display: flex;
			align-items: center;
			gap: 6px;
			flex-wrap: wrap;
		}

		.pill {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			padding: 3px 8px;
			border-radius: 999px;
			font-weight: 600;
			font-size: 0.8rem;
		}

		.pill.male {
			color: ${THEME_MAP["color-info-700"]};
			background: ${THEME_MAP["color-info-transparent-200"]};
		}

		.pill.female {
			color: ${THEME_MAP["color-danger-700"]};
			background: ${THEME_MAP["color-danger-transparent-200"]};
		}

		.pill.other {
			color: ${THEME_MAP["color-info-700"]};
			background: ${THEME_MAP["color-info-transparent-200"]};
		}

		.pill.single {
			color: ${THEME_MAP["color-warning-800"]};
			background: ${THEME_MAP["color-warning-transparent-200"]};
		}

		.pill.taken {
			color: ${THEME_MAP["color-success-800"]};
			background: ${THEME_MAP["color-success-transparent-200"]};
		}

		.pill.relationship-other {
			color: ${THEME_MAP["color-basic-800"]};
			background: ${THEME_MAP["color-basic-300"]};
		}

		.pill.muted {
			color: ${THEME_MAP["color-basic-700"]};
			background: ${THEME_MAP["color-basic-300"]};
		}

		.cell-pill {
			display: inline-flex;
			align-items: center;
			padding: 2px 8px;
			border-radius: 999px;
			font-size: 0.85rem;
			font-weight: 600;
		}

		.cell-pill.admin-badge {
			color: ${THEME_MAP["color-primary-700"]};
			background: ${THEME_MAP["color-primary-transparent-200"]};
			font-weight: 700;
		}

		.subtext {
			color: ${THEME_MAP["color-basic-500"]};
			font-size: 0.85rem;
		}

		.row-main {
			display: flex;
			align-items: center;
			gap: 12px;
			padding: 10px 16px;
			color: ${THEME_MAP["color-basic-700"]};
			transition: background 0.12s ease;

			.user-row:hover & {
				background: ${THEME_MAP["color-primary-transparent-100"]};
			}
		}

		.delete-cell {
			flex: 0 0 44px;
			display: flex;
			align-items: center;
			justify-content: center;

			.icon-button {
				color: inherit;
			}
		}

		.expand-panel {
			padding: 12px 16px 16px;
			background: ${THEME_MAP["color-basic-200"]};
			border-top: 1px solid ${THEME_MAP["color-basic-300"]};
		}

		.expand-grid {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
			gap: 12px;
			margin-bottom: 12px;
		}

		.expand-actions {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 12px;
			flex-wrap: wrap;
		}

		.expand-actions .error-text {
			color: ${THEME_MAP["color-danger-700"]};
			font-weight: 600;
		}

		.expand-actions .buttons {
			display: flex;
			align-items: center;
			gap: 8px;
		}
	}

	.users-container .user-row:last-child {
		margin-bottom: 0;
	}
`;

/* ── helper functions for pills ─────────────────────────────────── */

function formatBirthdate(raw) {
	if (!raw) return { ageText: "" };
	try {
		const d = raw?.toDate ? raw.toDate() : new Date(raw);
		if (isNaN(d)) return { ageText: "" };
		const diff = Date.now() - d.getTime();
		const age = Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
		return { ageText: age > 0 ? `${age}y` : "" };
	} catch {
		return { ageText: "" };
	}
}

function getGenderClass(g) {
	if (!g) return "";
	const lc = g.toLowerCase();
	if (lc === "male") return "male";
	if (lc === "female") return "female";
	return "other";
}

function getRelationshipClass(r) {
	if (!r) return "";
	const lc = r.toLowerCase();
	if (lc === "single") return "single";
	if (lc === "taken" || lc === "in a relationship" || lc === "married") return "taken";
	return "relationship-other";
}

/* ── UserListItem ──────────────────────────────────────────────── */

function UserListItem({ userSnapshot, expanded, onToggle, onDelete }) {
	const [currentRef] = useState(userSnapshot.ref);
	const [user, setUser] = useState({
		id: userSnapshot?.id,
		...(userSnapshot?.data() || {})
	});
	const [form, setForm] = useState({
		fullName: "",
		gender: "",
		relationship: "",
		birthdate: "",
		isAdmin: false
	});
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (currentRef) {
			return currentRef.onSnapshot((snap) => {
				if (snap) {
					const data = snap.data();
					setUser({ id: snap.id, ...data });
				}
			});
		}
	}, [currentRef]);

	const email = user?.email || "(anonymous)";
	const fullName = user?.fullName || user?.full_name || user?.name || [
		user?.firstName || user?.first_name || "",
		user?.lastName || user?.last_name || ""
	].filter(Boolean).join(" ");
	const gender = user?.gender || "";
	const relationship = user?.relationshipStatus || "";
	const { ageText } = formatBirthdate(
		user?.birthday || user?.birthdate || user?.birthday_at
	);
	const genderClass = getGenderClass(gender);
	const relationshipClass = getRelationshipClass(relationship);
	const isAdmin = !!user?.isAdmin;
	const visibility = user?.visibility || "";

	const handleFieldChange = useCallback((key) => (event) => {
		setForm((prev) => ({ ...prev, [key]: event.target.value }));
	}, []);

	const handleAdminToggle = useCallback((event) => {
		setForm((prev) => ({ ...prev, isAdmin: event.target.checked }));
	}, []);

	const handleSave = useCallback(async (event) => {
		event.stopPropagation();
		if (!currentRef) return;
		setSaving(true);
		setError("");
		try {
			const payload = {
				fullName: form.fullName || null,
				gender: form.gender || null,
				relationshipStatus: form.relationship || null,
				isAdmin: !!form.isAdmin
			};

			payload.birthday = form.birthdate ? dateInputToISOString(form.birthdate) : null;

			await currentRef.update(payload);
		} catch (err) {
			captureError(err, { view: "Users", action: "saveUser" });
			setError(err?.message || "Failed to save user");
		} finally {
			setSaving(false);
		}
	}, [currentRef, form.birthdate, form.fullName, form.gender, form.relationship, form.isAdmin]);

	const handleCancel = useCallback((event) => {
		event.stopPropagation();
		const birthDateValue = user?.birthday || user?.birthdate || user?.birthday_at;
		setForm({
			fullName,
			gender,
			relationship,
			birthdate: formatDateInput(birthDateValue),
			isAdmin
		});
		setError("");
	}, [fullName, gender, relationship, isAdmin, user?.birthday, user?.birthdate, user?.birthday_at]);

	useEffect(() => {
		const birthDateValue = user?.birthday || user?.birthdate || user?.birthday_at;
		setForm({
			fullName,
			gender,
			relationship,
			birthdate: formatDateInput(birthDateValue),
			isAdmin
		});
		setError("");
	}, [fullName, gender, relationship, isAdmin, user?.birthday, user?.birthdate, user?.birthday_at, user?.id]);

	return (
		<div
			className={`user-row ${expanded ? "expanded" : ""}`}
			onClick={onToggle}
			onKeyDown={(e) => {
				const target = e.target;
				const tag = (target?.tagName || "").toLowerCase();
				if (tag === "input" || tag === "select" || tag === "textarea" || tag === "button" || target?.isContentEditable) {
					return;
				}
				if (e.key === "Enter") {
					e.preventDefault();
					onToggle();
				}
			}}
			role="button"
			tabIndex={0}
		>
			<div className="row-main">
				<div className="name-email" style={{ flex: "1 1 auto" }}>
					<span className="email">{email}</span>
					{fullName ? <span className="name">{fullName}</span> : null}
				</div>
				<div className="pills-cell">
					{gender ? <span className={`pill ${genderClass}`}>{gender}</span> : null}
					{relationship ? <span className={`pill ${relationshipClass}`}>{relationship}</span> : null}
					{ageText ? <span className="pill muted">{ageText}</span> : null}
				</div>
				<div className="cell" style={{ flex: "0 0 80px" }}>
					{isAdmin ? <span className="cell-pill admin-badge">Admin</span> : <span className="subtext">—</span>}
				</div>
				<div className="delete-cell">
						<MuiIconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							onDelete({ id: user.id, email });
						}}
						className="icon-button"
						title="Delete user"
					>
						<DeleteOutline fontSize="small" />
					</MuiIconButton>
				</div>
			</div>
			{expanded ? (
				<div className="expand-panel" onClick={(e) => e.stopPropagation()}>
					<div className="expand-grid">
						<TextField
							label="Full name"
							size="small"
							value={form.fullName}
							onChange={handleFieldChange("fullName")}
							fullWidth
						/>
						<TextField
							select
							label="Gender"
							size="small"
							value={form.gender}
							onChange={handleFieldChange("gender")}
							fullWidth
						>
							<MenuItem value="Male">Male</MenuItem>
							<MenuItem value="Female">Female</MenuItem>
							<MenuItem value="Other">Other</MenuItem>
						</TextField>
						<TextField
							select
							label="Relationship"
							size="small"
							value={form.relationship}
							onChange={handleFieldChange("relationship")}
							fullWidth
						>
							<MenuItem value="Single">Single</MenuItem>
							<MenuItem value="Taken">Taken</MenuItem>
						</TextField>
						<TextField
							label="Birthday"
							type="date"
							size="small"
							value={form.birthdate}
							onChange={handleFieldChange("birthdate")}
							InputLabelProps={{ shrink: true }}
							fullWidth
						/>
						<FormControlLabel
							control={
								<Switch
									checked={!!form.isAdmin}
									onChange={handleAdminToggle}
									color="primary"
									inputProps={{ "aria-label": "admin toggle" }}
								/>
							}
							label="Admin"
						/>
						<TextField
							label="Visibility"
							value={visibility || "—"}
							size="small"
							InputProps={{ readOnly: true }}
							disabled
						/>
					</div>
					<div className="expand-actions">
						{error ? <span className="error-text">{error}</span> : <span />}
						<div className="buttons">
							<Button variant="text" size="small" onClick={handleCancel} disabled={saving}>
								Cancel
							</Button>
							<Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
								{saving ? "Saving..." : "Save"}
							</Button>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
}

function formatDateInput(value) {
	if (!value) return "";
	try {
		const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
		if (Number.isNaN(date.getTime())) return "";
		const year = date.getFullYear();
		const month = `${date.getMonth() + 1}`.padStart(2, "0");
		const day = `${date.getDate()}`.padStart(2, "0");
		return `${year}-${month}-${day}`;
	} catch (_err) {
		return "";
	}
}

function dateInputToISOString(dateStr) {
	if (!dateStr || typeof dateStr !== "string") return null;
	const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (!match) return null;
	const [, y, m, d] = match;
	return `${y}-${m}-${d}T05:00:00.000Z`;
}

